// {{WORKTREE_ROOT}}/picosponsorconsole/app/hooks/useActionFetcher.ts
// PLAN2 WP10 — the only way client code submits to an action (C-104; a guard fails a raw useFetcher that posts).
// submit() appends the root loader's csrf when absent; resubmit() replays the last submission with the CURRENT token
// (after a step-up the root revalidated and minted a new one); pendingIntent names the pressed button (C-125).
import { useCallback, useMemo, useRef } from "react";
import { useFetcher, useRouteLoaderData, type SubmitOptions } from "@remix-run/react";

type Target = FormData | Record<string, string>;

export function useCsrfToken(): string {
  const root = useRouteLoaderData("root") as { csrf?: string } | undefined;
  return root?.csrf ?? "";
}

export function withCsrf(target: Target, csrf: string, replace = false): Target {
  if (target instanceof FormData) {
    if (target.has("csrf") && !replace) return target;
    const copy = new FormData();
    target.forEach((v, k) => { if (k !== "csrf") copy.append(k, v); });
    copy.set("csrf", csrf);
    return copy;
  }
  if ("csrf" in target && !replace) return target;
  return { ...target, csrf };
}

export function useActionFetcher<T = unknown>(opts?: { key?: string }) {
  const fetcher = useFetcher<T>(opts);
  const csrf = useCsrfToken();
  const last = useRef<{ target: Target; options: SubmitOptions } | null>(null);
  const submit = useCallback((target: Target, options: SubmitOptions = {}) => {
    const t = withCsrf(target, csrf);
    last.current = { target: t, options };
    fetcher.submit(t, { method: "post", ...options });
  }, [fetcher, csrf]);
  const resubmit = useCallback(() => {
    if (!last.current) return;
    fetcher.submit(withCsrf(last.current.target, csrf, true), { method: "post", ...last.current.options });
  }, [fetcher, csrf]);
  const pendingIntent = fetcher.state !== "idle" ? String(fetcher.formData?.get("intent") ?? "") || null : null;
  return useMemo(() => ({ ...fetcher, submit, resubmit, pendingIntent }), [fetcher, submit, resubmit, pendingIntent]);
}
